import { refreshOnVariantUpdate } from '@theme/pdp-utils';

const DAY_MS = 86400000;
const HOUR_MS = 3600000;

/** @type {Map<string, Intl.DateTimeFormat>} Built once per time zone: the countdown reads the clock every second. */
const wallClockFormatters = new Map();

/**
 * The wall-clock time in `timeZone` for an instant, packed into a UTC timestamp (its UTC fields hold the local parts).
 * @param {number} instant - Milliseconds since the epoch
 * @param {string} timeZone
 * @returns {number}
 */
function wallClock(instant, timeZone) {
  let formatter = wallClockFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    wallClockFormatters.set(timeZone, formatter);
  }
  const parts = formatter.formatToParts(new Date(instant));
  /** @param {string} type */
  const get = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const ms = ((instant % 1000) + 1000) % 1000;
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'), ms);
}

/**
 * Countdown to the next cutoff and the "Get it by" day.
 * @param {number} nowMs
 * @param {number} cutoffHour - 0 to 23, in `timeZone`
 * @param {number} deliveryDays
 * @param {Set<number>} skipDays - Weekdays (0 = Sunday) with no delivery
 * @param {string} [timeZone]
 * @returns {{ remainingSeconds: number, deliveryDay: number, daysAway: number, deliverySeconds: number }}
 *   remainingSeconds runs to the next cutoff; deliveryDay is the UTC-midnight timestamp of the local date; daysAway
 *   counts calendar days from today's local date to it; deliverySeconds runs to the end of the delivery day
 */
export function computeDelivery(nowMs, cutoffHour, deliveryDays, skipDays, timeZone = 'Asia/Riyadh') {
  const wall = wallClock(nowMs, timeZone);
  const today = Math.floor(wall / DAY_MS) * DAY_MS;
  let cutoffDay = today;
  let cutoff = cutoffDay + cutoffHour * HOUR_MS;
  if (wall >= cutoff) {
    cutoffDay += DAY_MS;
    cutoff += DAY_MS;
  }
  const skip = skipDays.size >= 7 ? new Set() : skipDays;
  const blocked = (/** @type {number} */ day) => skip.has(new Date(day).getUTCDay());
  let day = cutoffDay;
  for (let step = 0; step < Math.max(0, deliveryDays); step++) {
    day += DAY_MS;
    while (blocked(day)) day += DAY_MS;
  }
  while (blocked(day)) day += DAY_MS;
  return {
    remainingSeconds: Math.max(0, Math.floor((cutoff - wall) / 1000)),
    deliveryDay: day,
    daysAway: Math.round((day - today) / DAY_MS),
    deliverySeconds: Math.max(0, Math.floor((day + DAY_MS - wall) / 1000)),
  };
}

/** @type {Map<string, Intl.DateTimeFormat>} The arrival date's format, per language. */
const arrivalFormats = new Map();

/**
 * The arrival date of the cart page (assets/fd-cart-eta.js) and the quick add pop-up (assets/fd-quick-add.js): the rule
 * above with the cart's theme settings, read as weekday, day and month in the page's language with the Gregorian
 * calendar and Latin digits.
 * @param {DOMStringMap} settings - The element's data attributes: data-cutoff-hour, data-days, data-skip-days (weekdays
 *   with no delivery, "5,6") and data-lang
 * @param {number} [nowMs]
 * @returns {string}
 */
export function arrivalDate(settings, nowMs = Date.now()) {
  const skip = new Set(
    (settings.skipDays || '')
      .split(',')
      .filter((day) => day !== '')
      .map(Number)
  );
  const { deliveryDay } = computeDelivery(nowMs, Number(settings.cutoffHour ?? 18), Number(settings.days ?? 2), skip);
  const lang = settings.lang || 'en';
  let format = arrivalFormats.get(lang);
  if (!format) {
    const options = /** @type {Intl.DateTimeFormatOptions} */ ({
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
    try {
      format = new Intl.DateTimeFormat(`${lang}-u-ca-gregory-nu-latn`, options);
    } catch {
      format = new Intl.DateTimeFormat('en-u-ca-gregory-nu-latn', options);
    }
    arrivalFormats.set(lang, format);
  }
  // deliveryDay is the delivery date's midnight as a UTC timestamp, so it is formatted in UTC.
  return format.format(new Date(deliveryDay));
}

/**
 * The countdown head of the delivery card. Numbers and the date come from the block's settings; data-countdown-mode
 * picks the countdown: "delivery" (DAYS : HRS to the end of the delivery day) or "cutoff" (HRS : MIN : SEC).
 */
class PdpDelivery extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  /** @type {Intl.DateTimeFormat | undefined} */
  #dateFormatter;

  #dateLang = '';

  /** @type {Intl.PluralRules | undefined} */
  #pluralRules;

  #pluralLang = '';

  connectedCallback() {
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #tick() {
    const skip = new Set(
      (this.dataset.skipDays || '')
        .split(',')
        .filter((value) => value !== '')
        .map(Number)
    );
    const { remainingSeconds, deliveryDay, daysAway, deliverySeconds } = computeDelivery(
      Date.now(),
      Number(this.dataset.cutoffHour ?? 18),
      Number(this.dataset.deliveryDays ?? 2),
      skip,
      this.dataset.timeZone || 'Asia/Riyadh'
    );
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
    if (this.dataset.countdownMode === 'cutoff') {
      // HRS : MIN : SEC to today's order cutoff
      this.#set('[data-pdp-hrs]', pad(Math.floor(remainingSeconds / 3600)));
      this.#set('[data-pdp-min]', pad(Math.floor((remainingSeconds % 3600) / 60)));
      this.#set('[data-pdp-sec]', pad(remainingSeconds % 60));
    } else {
      // DAYS : HRS to the end of the delivery day
      this.#set('[data-pdp-days]', pad(Math.floor(deliverySeconds / 86400)));
      this.#set('[data-pdp-hrs]', pad(Math.floor((deliverySeconds % 86400) / 3600)));
    }
    this.#set('[data-pdp-delivery-date]', this.#formatDate(deliveryDay));
    const away = this.querySelector('[data-pdp-days-away]');
    if (away instanceof HTMLElement) this.#set('[data-pdp-days-away]', this.#formatDaysAway(away, daysAway));
  }

  /**
   * "today", "tomorrow" or "in N days" with the plural form of the page language. The wordings come from the locale
   * files through the element's data attributes; [days] stands for the number.
   * @param {HTMLElement} element
   * @param {number} days
   */
  #formatDaysAway(element, days) {
    if (days <= 0) return element.dataset.today ?? '';
    if (days === 1) return element.dataset.tomorrow ?? '';
    const lang = document.documentElement.lang || 'en';
    if (!this.#pluralRules || this.#pluralLang !== lang) {
      try {
        this.#pluralRules = new Intl.PluralRules(lang);
      } catch {
        this.#pluralRules = new Intl.PluralRules('en');
      }
      this.#pluralLang = lang;
    }
    const template = element.dataset[this.#pluralRules.select(days)] || element.dataset.other || '';
    return template.replace('[days]', String(days));
  }

  /**
   * @param {string} selector
   * @param {string} text
   */
  #set(selector, text) {
    const element = this.querySelector(selector);
    if (element && element.textContent !== text) element.textContent = text;
  }

  /**
   * Gregorian calendar and Latin digits in every language; weekday and month names follow the page language.
   * @param {number} day
   */
  #formatDate(day) {
    const lang = document.documentElement.lang || 'en';
    if (!this.#dateFormatter || this.#dateLang !== lang) {
      const options = /** @type {Intl.DateTimeFormatOptions} */ ({
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      });
      try {
        this.#dateFormatter = new Intl.DateTimeFormat(`${lang}-u-ca-gregory-nu-latn`, options);
      } catch {
        this.#dateFormatter = new Intl.DateTimeFormat('en-u-ca-gregory-nu-latn', options);
      }
      this.#dateLang = lang;
    }
    return this.#dateFormatter.format(new Date(day));
  }
}

/** The server-rendered low-stock line. Refreshed from the page Horizon fetches for the chosen variant. */
class PdpDeliveryStock extends HTMLElement {
  /** @type {(() => void) | undefined} */
  #unsubscribe;

  connectedCallback() {
    this.#unsubscribe = refreshOnVariantUpdate(this);
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
  }
}

if (!customElements.get('pdp-delivery')) customElements.define('pdp-delivery', PdpDelivery);
if (!customElements.get('pdp-delivery-stock')) customElements.define('pdp-delivery-stock', PdpDeliveryStock);
